import type { LoginCredentials } from "./auth.types";
import type { AuthSession } from "./models/session.model";
import type { AuthTokens } from "./models/auth-tokens.model";
import type { PermissionAction } from "./models/permission.model";
import type { Role } from "./models/role.model";
import type { User } from "./models/user.model";
import type { Tenant } from "../tenant/models/tenant.model";
import { getUserPermissionKeys } from "../rbac/access";
import {
  clearStoredSession,
  getAuthEpoch,
  isAuthEpochCurrent,
  isTokenExpired,
  loadStoredSession,
  saveSession,
  updateTokens,
} from "./auth.storage";
import { purgeAuthState } from "./purge-auth-state";
import type {
  CreateIamSessionClientOptions,
  IamLoginResult,
  IamRefreshResult,
  IamSessionClient,
} from "./iam-session.types";
import {
  AUTH_REFRESH_PROBE_INTERVAL_MS,
  AUTH_REFRESH_PROBE_KEY,
  DEMO_TENANT_ID,
  DEMO_TENANT_SLUG,
} from "./auth.constants";
import {
  expandIamPermissionsForRh,
  isIamPlatformSuperAdmin,
  resolveIamRoleSlug,
} from "./iam-rh-permissions";
import { isAuthRejectionError } from "./auth-errors";

const IAM_REQUEST_TIMEOUT_MS = 8_000;

function withIamTimeout<T>(promise: Promise<T>, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(
        () => reject(new Error(`iam_timeout:${label}`)),
        IAM_REQUEST_TIMEOUT_MS,
      );
    }),
  ]);
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const segment = token.split(".")[1];
  if (!segment) return {};
  const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(atob(normalized)) as Record<string, unknown>;
}

function resolveRhTenantId(tenantId: string, tenantSlug: string): string {
  return tenantSlug === DEMO_TENANT_SLUG ? DEMO_TENANT_ID : tenantId;
}

function resolveTenantNameFromPayload(
  payload: Record<string, unknown>,
  tenantSlug: string,
  resolveTenantDisplayName?: (slug: string) => string | undefined,
  defaultTenantName = "ITaaS",
): string {
  const fromJwt = typeof payload.tenantName === "string" ? payload.tenantName.trim() : "";
  if (fromJwt) return fromJwt;
  const fromCatalog = resolveTenantDisplayName?.(tenantSlug)?.trim();
  if (fromCatalog) return fromCatalog;
  return defaultTenantName;
}

function mapPermissionsToRole(
  permissions: string[],
  rawIamPermissions: string[],
  tenantSlug: string,
): Role {
  const slug = resolveIamRoleSlug(rawIamPermissions, tenantSlug);
  return {
    id: `iam-role-${slug.toLowerCase().replace(/_/g, "-")}`,
    name: slug,
    slug,
    permissions: permissions.map((key) => {
      const parts = key.split(".");
      const action = parts.pop() ?? "read";
      const resource = parts.pop() ?? key;
      const domain = parts.join(".") || resource;
      return {
        resource: domain,
        action: action as PermissionAction,
        key,
      };
    }),
  };
}

function mapIamUserToCoreUser(
  iamUser: Pick<IamLoginResult["user"], "id" | "email" | "firstName" | "lastName">,
  permissions: string[],
  rawIamPermissions: string[],
  tenantSlug: string,
): User {
  const now = new Date().toISOString();
  const isSuperAdmin = isIamPlatformSuperAdmin(rawIamPermissions, tenantSlug);
  return {
    id: iamUser.id,
    email: iamUser.email,
    firstName: iamUser.firstName,
    lastName: iamUser.lastName,
    displayName: `${iamUser.firstName} ${iamUser.lastName}`.trim(),
    status: "active",
    isActive: true,
    isSuperAdmin,
    roles: [mapPermissionsToRole(permissions, rawIamPermissions, tenantSlug)],
    createdAt: now,
    updatedAt: now,
  };
}

function syncSessionFromAccessToken(
  session: AuthSession,
  accessToken: string,
  resolveTenantDisplayName?: (slug: string) => string | undefined,
  defaultTenantName = "ITaaS",
): AuthSession {
  const payload = decodeJwtPayload(accessToken);
  const rawPermissions = Array.isArray(payload.permissions)
    ? (payload.permissions as string[])
    : getUserPermissionKeys(session.user);
  const resolvedSlug = String(payload.tenantSlug ?? session.tenant.slug);
  const permissions = expandIamPermissionsForRh(rawPermissions, resolvedSlug);
  const licensedModules = Array.isArray(payload.modules)
    ? (payload.modules as string[])
    : (session.licensedModules ?? []);
  const moduleEditions =
    payload.moduleEditions && typeof payload.moduleEditions === "object"
      ? (payload.moduleEditions as Record<string, string>)
      : session.moduleEditions;

  const rhTenantId = resolveRhTenantId(
    String(payload.tenantId ?? session.tenant.id),
    resolvedSlug,
  );

  const user = mapIamUserToCoreUser(
    {
      id: session.user.id,
      email: session.user.email,
      firstName: session.user.firstName,
      lastName: session.user.lastName,
    },
    permissions,
    rawPermissions,
    resolvedSlug,
  );

  return {
    ...session,
    user,
    licensedModules,
    moduleEditions,
    trialReadOnly: payload.trialReadOnly === true,
    tenant: {
      ...session.tenant,
      id: rhTenantId,
      slug: resolvedSlug,
      name: resolveTenantNameFromPayload(
        payload,
        resolvedSlug,
        resolveTenantDisplayName,
        session.tenant.name || defaultTenantName,
      ),
    },
    membership: {
      ...session.membership,
      tenantId: rhTenantId,
    },
  };
}

export function createIamSessionClient(
  options: CreateIamSessionClientOptions,
): IamSessionClient {
  const {
    iamRequest,
    tenantSlug,
    resolveTenantSlug,
    resolveTenantDisplayName,
    defaultTenantName = "ITaaS",
  } = options;

  function mapIamLoginToSession(result: IamLoginResult): AuthSession {
    const payload = decodeJwtPayload(result.accessToken);
    const resolvedSlug = String(payload.tenantSlug ?? tenantSlug);
    const tenantId = resolveRhTenantId(String(payload.tenantId ?? ""), resolvedSlug);
    const licensedModules = Array.isArray(payload.modules)
      ? (payload.modules as string[])
      : [];
    const moduleEditions =
      payload.moduleEditions && typeof payload.moduleEditions === "object"
        ? (payload.moduleEditions as Record<string, string>)
        : undefined;
    const now = new Date().toISOString();
    const rawIamPermissions = result.user.permissions;

    const tenant: Tenant = {
      id: tenantId,
      name: resolveTenantNameFromPayload(
        payload,
        resolvedSlug,
        resolveTenantDisplayName,
        defaultTenantName,
      ),
      slug: resolvedSlug,
      status: "active",
      planId: "plan-business-pro",
      subscriptionStatus: "active",
      timezone: "Africa/Kinshasa",
      language: "fr",
      currency: "USD",
      createdAt: now,
      updatedAt: now,
    };

    const user = mapIamUserToCoreUser(
      result.user,
      expandIamPermissionsForRh(rawIamPermissions, resolvedSlug),
      rawIamPermissions,
      resolvedSlug,
    );

    return {
      user,
      tenant,
      membership: {
        userId: user.id,
        tenantId: tenant.id,
        roleIds: user.roles.map((role) => role.id),
      },
      tokens: {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAt: Date.now() + result.expiresIn * 1000,
      },
      licensedModules,
      moduleEditions,
      trialReadOnly: payload.trialReadOnly === true,
    };
  }

  async function refresh(refreshToken: string): Promise<AuthTokens> {
    const epochAtStart = getAuthEpoch();
    const result = await iamRequest<IamRefreshResult>("/auth/refresh", {
      method: "POST",
      body: { refreshToken },
    });

    if (!isAuthEpochCurrent(epochAtStart)) {
      throw new Error("auth_stale");
    }

    const tokens: AuthTokens = {
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt: Date.now() + result.expiresIn * 1000,
    };

    const session = loadStoredSession();
    if (session) {
      const next = syncSessionFromAccessToken(
        { ...session, tokens },
        tokens.accessToken,
        resolveTenantDisplayName,
        defaultTenantName,
      );
      if (isAuthEpochCurrent(epochAtStart)) {
        saveSession(next);
      }
    } else if (isAuthEpochCurrent(epochAtStart)) {
      updateTokens(tokens);
    }

    return tokens;
  }

  async function login(credentials: LoginCredentials): Promise<AuthSession> {
    const resolvedSlug =
      credentials.tenantSlug?.trim().toLowerCase() ||
      resolveTenantSlug?.(credentials.email) ||
      tenantSlug;
    const result = await iamRequest<IamLoginResult>("/auth/login", {
      method: "POST",
      body: {
        tenantSlug: resolvedSlug,
        email: credentials.email.trim().toLowerCase(),
        password: credentials.password,
      },
    });

    const session = mapIamLoginToSession(result);
    saveSession(session);
    return session;
  }

  async function logout(): Promise<void> {
    const session = loadStoredSession();
    if (session?.tokens.refreshToken) {
      try {
        await iamRequest("/auth/logout", {
          method: "POST",
          accessToken: session.tokens.accessToken,
          body: { refreshToken: session.tokens.refreshToken },
        });
      } catch {
        // Réseau ou session déjà révoquée — on purge localement quand même.
      }
    }
    purgeAuthState();
  }

  async function verifySession(): Promise<AuthSession | null> {
    const epochAtStart = getAuthEpoch();
    let session = loadStoredSession();
    if (!session) return null;

    if (isTokenExpired(session.tokens.expiresAt)) {
      if (session.tokens.refreshToken) {
        try {
          await withIamTimeout(refresh(session.tokens.refreshToken), "refresh");
          if (!isAuthEpochCurrent(epochAtStart)) return null;
          return loadStoredSession();
        } catch (error) {
          if (isAuthEpochCurrent(epochAtStart) && isAuthRejectionError(error)) {
            purgeAuthState();
          }
          return isAuthEpochCurrent(epochAtStart) ? loadStoredSession() : null;
        }
      }
      if (isAuthEpochCurrent(epochAtStart)) {
        purgeAuthState();
      }
      return null;
    }

    if (session.tokens.refreshToken && typeof localStorage !== "undefined") {
      const lastProbe = Number(localStorage.getItem(AUTH_REFRESH_PROBE_KEY) || 0);
      if (Date.now() - lastProbe >= AUTH_REFRESH_PROBE_INTERVAL_MS) {
        localStorage.setItem(AUTH_REFRESH_PROBE_KEY, String(Date.now()));
        try {
          await withIamTimeout(refresh(session.tokens.refreshToken), "probe");
          if (!isAuthEpochCurrent(epochAtStart)) return null;
          session = loadStoredSession();
          if (!session) return null;
        } catch (error) {
          if (isAuthEpochCurrent(epochAtStart) && isAuthRejectionError(error)) {
            purgeAuthState();
            return null;
          }
        }
      }
    }

    try {
      session = syncSessionFromAccessToken(
        session,
        session.tokens.accessToken,
        resolveTenantDisplayName,
        defaultTenantName,
      );
      if (!isAuthEpochCurrent(epochAtStart)) return null;
      saveSession(session);
      return session;
    } catch {
      if (isAuthEpochCurrent(epochAtStart)) {
        purgeAuthState();
      }
      return null;
    }
  }

  async function forceRefreshSession(): Promise<AuthSession | null> {
    const session = loadStoredSession();
    if (!session?.tokens.refreshToken) return session;

    try {
      await refresh(session.tokens.refreshToken);
      return loadStoredSession();
    } catch (error) {
      if (isAuthRejectionError(error)) {
        purgeAuthState();
        return null;
      }
      return loadStoredSession();
    }
  }

  return {
    login,
    logout,
    refreshToken: refresh,
    verifySession,
    forceRefreshSession,
  };
}
